// Considered: ContextFlowCanvasOrganism — rejected because it renders a linear chain with
// outwards/inwards connectors; an arc's transitions branch, skip stages and loop back, and need
// labelled, selectable edges the linear chain has no shape for. See spec Reuse Audit
// (arc-editor-canvas-v1.md).
import React from 'react';
import { useTranslation } from 'localization';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import TagAtom from '../../atoms/Tag/TagAtom';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import './ArcFlowViewMolecule.scss';

/** Node width, horizontal gap and row baseline — the same geometry as the prototype's
 * `AC_NODE_W`/`AC_GAP`/`AC_ROW_Y` (`arc-flow.jsx:9`), so the two views read as one arc. */
const NODE_WIDTH = 168;
const NODE_GAP = 96;
const ROW_Y = 150;

export interface ArcFlowViewNode {
  id: string;
  name: string;
  isTerminal?: boolean;
  keyDate: {
    /** Already-localized chip text (`acStageWhen(n).text`). */
    text: string;
    vacant?: boolean;
    offsetOk?: boolean;
  };
}

export type ArcFlowTransitionKind = 'manual' | 'auto' | 'calculated';

export interface ArcFlowViewTransition {
  id: string;
  from: string;
  to: string;
  kind: ArcFlowTransitionKind;
  /** Iconify `lucide:*` name for the kind (`AC_TRANS_KINDS[kind].glyph`). */
  glyph: string;
  label: string;
  /** Already-localized sub-label — the calculated condition or the resolved anchor
   * (`edgeWhen(t)`); absent for a manual transition with no override role. */
  when?: string;
}

export type ArcFlowSelection =
  | { kind: 'stage'; id: string }
  | { kind: 'transition'; id: string }
  | null;

export interface ArcFlowViewMoleculeProps {
  nodes: ArcFlowViewNode[];
  transitions: ArcFlowViewTransition[];
  editable?: boolean;
  selection?: ArcFlowSelection;
  onSelectStage: (node: ArcFlowViewNode) => void;
  onSelectTransition: (transition: ArcFlowViewTransition) => void;
  onAddTransition?: () => void;
  className?: string;
}

interface EdgePath {
  d: string;
  dir: 'fwd' | 'skip' | 'back';
  mx: number;
  my: number;
}

/** The forward spine, skips and loops of an arc as a graph — the projection the stage x lane
 * grid can't draw (`ArcFlow`, `arc-flow.jsx:11-114`). */
const ArcFlowViewMolecule: React.FC<ArcFlowViewMoleculeProps> = ({
  nodes,
  transitions,
  editable = false,
  selection = null,
  onSelectStage,
  onSelectTransition,
  onAddTransition,
  className = '',
}) => {
  const { t } = useTranslation();

  const positioned = nodes.map((node, index) => ({
    ...node,
    x: 40 + index * (NODE_WIDTH + NODE_GAP),
    y: ROW_Y,
  }));
  const at = (id: string) => positioned.find((n) => n.id === id);
  const width = (positioned.length ? positioned[positioned.length - 1].x : 40) + NODE_WIDTH + 40;
  const height = ROW_Y + 190;

  const path = (transition: ArcFlowViewTransition): EdgePath | null => {
    const a = at(transition.from);
    const b = at(transition.to);
    if (!a || !b) {
      return null;
    }
    const ai = positioned.findIndex((n) => n.id === transition.from);
    const bi = positioned.findIndex((n) => n.id === transition.to);
    const ax = a.x + NODE_WIDTH;
    const ay = a.y + 34;
    const bx = b.x;
    const by = b.y + 34;

    if (bi === ai + 1) {
      return { d: `M${ax} ${ay} L${bx} ${by}`, dir: 'fwd', mx: (ax + bx) / 2, my: ay - 8 };
    }
    if (bi > ai + 1) {
      const top = a.y - 66;
      const acx = a.x + NODE_WIDTH / 2;
      const bcx = b.x + NODE_WIDTH / 2;
      return {
        d: `M${acx} ${a.y} C${acx} ${top}, ${bcx} ${top}, ${bcx} ${b.y}`,
        dir: 'skip',
        mx: (a.x + b.x) / 2 + NODE_WIDTH / 2,
        my: top + 16,
      };
    }
    const bot = a.y + 152;
    const acx = a.x + NODE_WIDTH / 2;
    const bcx = b.x + NODE_WIDTH / 2;
    return {
      d: `M${acx} ${a.y + 68} C${acx} ${bot}, ${bcx} ${bot}, ${bcx} ${b.y + 68}`,
      dir: 'back',
      mx: (a.x + b.x) / 2 + NODE_WIDTH / 2,
      my: bot - 10,
    };
  };

  return (
    <div className={`arc-flow-view ${className}`.trim()}>
      <div className="arc-flow-view__bar">
        <span className="arc-flow-view__hint" data-datum="canvas.every-path-through-this-arc-the-grid-dra" data-kind="hint">
          {t('studio.arcEditor.flowView.hint')}
        </span>
        {editable && (
          <ButtonAtom
            variant="text"
            size="small"
            icon={<GlyphAtom glyph="lucide:plus" aria-hidden />}
            label={t('studio.arcEditor.flowView.addTransition')}
            onClick={onAddTransition}
            data-datum="canvas.transition"
            data-kind="button"
          />
        )}
      </div>

      <div className="arc-flow-view__canvas" style={{ width, height }}>
        <svg width={width} height={height} className="arc-flow-view__svg" aria-hidden>
          <defs>
            <marker id="arc-flow-view-arrow" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto">
              <path d="M0 0 L8 4 L0 8 z" className="arc-flow-view__arrowhead" />
            </marker>
            <marker id="arc-flow-view-arrow-on" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto">
              <path d="M0 0 L8 4 L0 8 z" className="arc-flow-view__arrowhead" data-on="true" />
            </marker>
          </defs>
          {transitions.map((transition) => {
            const p = path(transition);
            if (!p) {
              return null;
            }
            const on = selection?.kind === 'transition' && selection.id === transition.id;
            return (
              <path
                key={transition.id}
                d={p.d}
                className="arc-flow-view__edge-line"
                data-dir={p.dir}
                data-on={on ? 'true' : undefined}
                strokeDasharray={transition.kind === 'calculated' ? '5 4' : undefined}
                markerEnd={on ? 'url(#arc-flow-view-arrow-on)' : 'url(#arc-flow-view-arrow)'}
              />
            );
          })}
        </svg>

        {transitions.map((transition) => {
          const p = path(transition);
          if (!p) {
            return null;
          }
          const on = selection?.kind === 'transition' && selection.id === transition.id;
          return (
            <button
              key={transition.id}
              type="button"
              className="arc-flow-view__edge"
              data-on={on ? 'true' : undefined}
              data-dir={p.dir}
              style={{ left: p.mx, top: p.my }}
              onClick={() => onSelectTransition(transition)}
              aria-label={t('studio.arcEditor.flowView.transitionAriaLabel', { label: transition.label })}
              data-container="each(transitions)"
              data-datum="canvas.button"
              data-kind="button"
            >
              <GlyphAtom glyph={`lucide:${transition.glyph}`} aria-hidden />
              <span>{transition.label}</span>
              {transition.when && <i>{transition.when}</i>}
            </button>
          );
        })}

        {positioned.map((node, index) => {
          const selected = selection?.kind === 'stage' && selection.id === node.id;
          const keyDateWarn = Boolean(node.keyDate.vacant) || node.keyDate.offsetOk === false;
          return (
            <button
              key={node.id}
              type="button"
              className="arc-flow-view__node"
              style={{ left: node.x, top: node.y, width: NODE_WIDTH }}
              data-selected={selected ? 'true' : undefined}
              data-terminal={node.isTerminal ? 'true' : undefined}
              onClick={() => onSelectStage(node)}
              data-container="each(nodes)"
              data-datum="canvas.card#2"
              data-kind="card"
            >
              <span className="arc-flow-view__node-n" data-datum="canvas.count" data-kind="count">
                {node.isTerminal ? <GlyphAtom glyph="lucide:flag" aria-hidden /> : index + 1}
              </span>
              <b className="arc-flow-view__node-name" data-datum="canvas.n-name" data-kind="strong">
                {node.name}
              </b>
              <TagAtom
                label={node.keyDate.text}
                tone={keyDateWarn ? 'warn' : undefined}
                icon={keyDateWarn ? 'lucide:help-circle' : 'lucide:clock'}
                size="small"
                data-datum="canvas.acstagewhen-n-text"
                data-kind="chip"
              />
              {node.isTerminal && (
                <TagAtom
                  label={t('studio.arcEditor.flowView.restsHere')}
                  size="small"
                  data-datum="canvas.the-arc-rests-here"
                  data-kind="chip"
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

ArcFlowViewMolecule.displayName = 'ArcFlowViewMolecule';

export default ArcFlowViewMolecule;
